/**
 * PermissionsRequiredPage
 *
 * Shown after a successful login when the signed-in user has no permissions
 * (no allowed actions and not an admin), so there are no tools/stores to render.
 * Previously this state rendered an empty screen; this page gives the user a
 * clear, actionable message and a way back to the login screen.
 *
 * Used for both web (Cloudscape) and app (Meridian) — it is built with the
 * shared Meridian primitives the platform uses in both modes. "Back to log in"
 * runs the unified logout via useLogout (web → WebLoginPage; React Native →
 * native-managed logout).
 */

import React from 'react'

import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'

import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { useLogout } from '@hooks/useLogout'

const PermissionsRequiredPage = (): React.ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const logout = useLogout()

    return (
        <Column
            alignmentHorizontal="center"
            spacingInset="600"
            spacing="400"
            height="100vh"
            data-testid="permissions-required-page"
        >
            <Column spacing="400" maxWidth={420} width="100%">
                <Text type="h500">{t(bundle, 'jwo-permissions-required-title')}</Text>

                <Column spacing="300">
                    <Text type="h300" data-testid="permissions-required-heading">
                        {t(bundle, 'jwo-permissions-required-message')}
                    </Text>
                    <Text type="b300" color="secondary">
                        {t(bundle, 'jwo-permissions-required-instructions')}
                    </Text>
                </Column>

                <Row widths="fill">
                    <Button
                        type="primary"
                        size="large"
                        onClick={logout}
                        data-testid="permissions-required-back-to-login"
                    >
                        {t(bundle, 'jwo-permissions-required-back-to-login')}
                    </Button>
                </Row>
            </Column>
        </Column>
    )
}

export default PermissionsRequiredPage
